import React, { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Plus, Trash2, Wrench, AlertTriangle, AlertCircle, Info, X, Check, RefreshCw, Edit3 } from "lucide-react";

const SEV = {
  info:        { label: "Info",         color: "bg-blue-50 text-blue-700 ring-blue-200",       Icon: Info },
  maintenance: { label: "Manutenzione", color: "bg-orange-50 text-orange-700 ring-orange-200", Icon: Wrench },
  warning:     { label: "Avviso",       color: "bg-amber-50 text-amber-800 ring-amber-200",    Icon: AlertTriangle },
  critical:    { label: "Critico",      color: "bg-rose-50 text-rose-700 ring-rose-200",       Icon: AlertCircle },
};

const STATUS = {
  scheduled: "Programmato",
  active: "Attivo",
  resolved: "Risolto",
};

export default function AdminMaintenancePanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | { new: true } | item
  const [toast, setToast] = useState("");

  function show(m) { setToast(m); setTimeout(() => setToast(""), 2500); }

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/maintenance");
      setItems(data || []);
    } catch (e) {
      show(formatErr(e.response?.data?.detail) || "Errore");
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function resolve(n) {
    if (!window.confirm(`Marcare "${n.title}" come risolto? L'avviso scomparirà dal banner.`)) return;
    try {
      await api.post(`/admin/maintenance/${n.id}/resolve`);
      show("Avviso risolto");
      load();
    } catch (e) { show(formatErr(e.response?.data?.detail) || "Errore"); }
  }
  async function remove(n) {
    if (!window.confirm(`Eliminare "${n.title}"? Questa azione è irreversibile.`)) return;
    try {
      await api.delete(`/admin/maintenance/${n.id}`);
      show("Avviso eliminato");
      load();
    } catch (e) { show(formatErr(e.response?.data?.detail) || "Errore"); }
  }

  return (
    <div className="space-y-5" data-testid="admin-maintenance">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-orange-600 inline-flex items-center gap-2">
            <Wrench className="h-3.5 w-3.5" /> System Status
          </div>
          <h2 className="font-display text-2xl font-medium tracking-tight mt-1">Manutenzioni e incidenti</h2>
          <p className="text-sm text-zinc-500 mt-1">Comunica manutenzioni, avvisi e disservizi a businesses e clienti.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="h-10 w-10 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center" title="Ricarica" data-testid="maintenance-refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
          <button onClick={() => setEditor({ new: true })} className="h-10 px-4 rounded-full bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium inline-flex items-center gap-2" data-testid="maintenance-new">
            <Plus className="h-4 w-4" /> Nuovo avviso
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 py-8">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-zinc-300 p-10 text-center">
          <Wrench className="h-10 w-10 mx-auto text-zinc-300" />
          <div className="mt-3 font-medium">Nessun avviso configurato</div>
          <div className="text-sm text-zinc-500 mt-1">Crea il primo per comunicare una manutenzione o un incidente.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((n) => {
            const sev = SEV[n.severity] || SEV.info;
            const SIcon = sev.Icon;
            return (
              <div key={n.id} className="rounded-2xl bg-white ring-1 ring-zinc-100 p-4" data-testid={`maintenance-row-${n.id.slice(0,8)}`}>
                <div className="flex items-start gap-3">
                  <span className={`inline-flex items-center justify-center h-9 w-9 rounded-xl ring-1 ${sev.color}`}>
                    <SIcon className="h-4 w-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium truncate">{n.title}</div>
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ring-1 ${sev.color}`}>{sev.label}</span>
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${n.status === "active" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : n.status === "resolved" ? "bg-zinc-100 text-zinc-500" : "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"}`}>
                        {STATUS[n.status] || n.status}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600 dark:bg-zinc-950/40">{audienceLabel(n.audience)}</span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                      {fmt(n.starts_at)}{n.ends_at ? ` → ${fmt(n.ends_at)}` : ""}{n.send_email ? " · 📧 email inviata" : ""}
                    </div>
                    <div className="text-xs text-zinc-600 mt-2 line-clamp-2 whitespace-pre-line">{n.message}</div>
                  </div>
                  <div className="flex gap-1">
                    {n.status !== "resolved" && (
                      <button onClick={() => resolve(n)} className="h-8 px-3 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-medium inline-flex items-center gap-1 dark:bg-emerald-950/40 dark:text-emerald-300" data-testid={`maintenance-resolve-${n.id.slice(0,8)}`}>
                        <Check className="h-3.5 w-3.5" /> Risolvi
                      </button>
                    )}
                    <button onClick={() => setEditor(n)} className="h-8 w-8 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center text-zinc-500" title="Modifica" data-testid={`maintenance-edit-${n.id.slice(0,8)}`}>
                      <Edit3 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => remove(n)} className="h-8 w-8 rounded-full hover:bg-rose-50 hover:text-rose-600 inline-flex items-center justify-center text-zinc-500 dark:bg-rose-950/40" title="Elimina" data-testid={`maintenance-delete-${n.id.slice(0,8)}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editor && (
        <NoticeEditor
          notice={editor.new ? null : editor}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); show("Avviso salvato"); }}
        />
      )}
      {toast && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-zinc-900 text-white text-sm px-4 py-2 rounded-full shadow-lg z-[70]">{toast}</div>}
    </div>
  );
}

function NoticeEditor({ notice, onClose, onSaved }) {
  const isNew = !notice;
  const [form, setForm] = useState({
    title: notice?.title || "",
    message: notice?.message || "",
    severity: notice?.severity || "maintenance",
    audience: notice?.audience || "all",
    status: notice?.status || (isNew ? "scheduled" : notice?.status),
    starts_at: notice?.starts_at ? toLocal(notice.starts_at) : "",
    ends_at: notice?.ends_at ? toLocal(notice.ends_at) : "",
    show_banner: notice?.show_banner ?? true,
    send_email: notice?.send_email ?? false,
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (form.title.trim().length < 3) { setErr("Titolo troppo breve"); return; }
    if (form.message.trim().length < 5) { setErr("Messaggio troppo breve"); return; }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        message: form.message.trim(),
        severity: form.severity,
        audience: form.audience,
        status: form.status,
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        show_banner: !!form.show_banner,
        send_email: !!form.send_email,
      };
      if (isNew) await api.post("/admin/maintenance", payload);
      else await api.put(`/admin/maintenance/${notice.id}`, payload);
      onSaved();
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Errore salvataggio");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end md:items-center justify-center p-4" onClick={onClose} data-testid="maintenance-editor">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto card-soft">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-orange-600">{isNew ? "Nuovo avviso" : "Modifica avviso"}</div>
            <h3 className="font-display text-2xl font-medium mt-1">{isNew ? "Crea avviso" : notice.title}</h3>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-3">
          {/* Preview banner */}
          <div className="rounded-2xl ring-1 ring-zinc-100 overflow-hidden">
            <div className="text-[10px] uppercase tracking-wider px-3 py-1 bg-zinc-50 text-zinc-500 dark:bg-zinc-950/40">Anteprima banner</div>
            <PreviewBanner severity={form.severity} title={form.title || "Titolo dell'avviso"} message={form.message || "Messaggio…"} ends_at={form.ends_at} />
          </div>

          <Field label="Titolo *">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-soft" maxLength={200} data-testid="editor-title" />
          </Field>
          <Field label="Messaggio *">
            <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-soft h-auto py-3" maxLength={4000} data-testid="editor-message" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Severità">
              <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="input-soft" data-testid="editor-severity">
                <option value="info">Info</option>
                <option value="maintenance">Manutenzione</option>
                <option value="warning">Avviso</option>
                <option value="critical">Critico</option>
              </select>
            </Field>
            <Field label="Stato">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input-soft" data-testid="editor-status">
                <option value="scheduled">Programmato</option>
                <option value="active">Attivo</option>
                <option value="resolved">Risolto</option>
              </select>
            </Field>
          </div>
          <Field label="Audience">
            <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="input-soft" data-testid="editor-audience">
              <option value="all">Tutti (businesses + clienti)</option>
              <option value="businesses_only">Solo businesses</option>
              <option value="customers_only">Solo clienti</option>
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Inizio (facoltativo)">
              <input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="input-soft" data-testid="editor-starts" />
            </Field>
            <Field label="Fine prevista (facoltativo)">
              <input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="input-soft" data-testid="editor-ends" />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.show_banner} onChange={(e) => setForm({ ...form, show_banner: e.target.checked })} data-testid="editor-show-banner" />
            Mostra banner in app
          </label>
          {isNew && (
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} data-testid="editor-send-email" />
              <span>Invia anche email broadcast (Resend) — comunicazione operativa di servizio (legittimo interesse)</span>
            </label>
          )}
          {err && <div className="text-sm text-red-600">{err}</div>}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 btn-secondary h-11 text-sm">Annulla</button>
            <button type="submit" disabled={busy} className="flex-1 btn-accent h-11 text-sm" data-testid="editor-submit">
              {busy ? "Salvataggio…" : isNew ? "Crea avviso" : "Salva modifiche"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PreviewBanner({ severity, title, message, ends_at }) {
  const cfg = {
    info: "bg-blue-600", maintenance: "bg-orange-600", warning: "bg-amber-500", critical: "bg-rose-700",
  }[severity] || "bg-blue-600";
  const label = { info: "Info", maintenance: "Manutenzione", warning: "Avviso", critical: "Critico" }[severity];
  return (
    <div className={`${cfg} text-white text-sm px-3 py-2.5`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-90">{label}</div>
      <div className="font-semibold mt-0.5 line-clamp-1">{title}</div>
      <div className="text-xs opacity-90 mt-0.5 line-clamp-2 whitespace-pre-line">{message}</div>
      {ends_at && <div className="text-[11px] opacity-80 mt-1">Fino a {ends_at}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function audienceLabel(a) {
  return { all: "Tutti", businesses_only: "Businesses", customers_only: "Clienti" }[a] || a;
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function toLocal(iso) {
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}
